import { useEffect, useState } from 'react'
import { DEFAULT_ROLE, speakable, ttsKey, type TtsRole } from '@diana/shared'

export { speakable }

/**
 * Polish read aloud: word cards, dialogue lines, and a listening script
 * whose recording is not uploaded yet (an uploaded recording always wins,
 * and the player says so on screen).
 *
 * First choice is the file the server made for exactly this text with a
 * natural voice (Google Chirp 3 HD, see apps/api/src/course/tts.ts). Only
 * when there is none does the device speak, with the best Polish voice it
 * has — the neural ones in Edge, Google's in Chrome, an enhanced Apple
 * voice if the student downloaded one — never just the first in the list.
 */

function synth(): SpeechSynthesis | null {
  return typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null
}

/** How natural a device voice sounds, from what its name gives away. */
function quality(v: SpeechSynthesisVoice): number {
  const n = `${v.name} ${v.voiceURI}`.toLowerCase()
  let s = 0
  if (/natural|neural|online/.test(n)) s += 50 // Edge: „Microsoft Zofia Online (Natural)”
  if (/premium/.test(n)) s += 40 // Apple, downloaded
  if (/enhanced|siri/.test(n)) s += 30 // Apple, downloaded
  if (/google/.test(n)) s += 25 // Chrome: „Google polski”
  if (!v.localService) s += 5
  if (/compact|paulina|espeak/.test(n)) s -= 20 // the robotic defaults
  return s
}

let chosen: SpeechSynthesisVoice | null = null
let chosenFrom = -1

function polishVoice(): SpeechSynthesisVoice | null {
  const s = synth()
  if (!s) return null
  const voices = s.getVoices()
  if (voices.length === chosenFrom && chosen) return chosen
  const polish = voices.filter((v) => v.lang.replace('_', '-').toLowerCase().indexOf('pl') === 0)
  chosen = polish.sort((a, b) => quality(b) - quality(a))[0] ?? null
  chosenFrom = voices.length
  return chosen
}

/* ── the server's recordings ─────────────────────────────────────────── */

/** Whether the server has recordings at all; asked once. Without them nothing is tried. */
let enabled: Promise<boolean> | null = null
function recordings(): Promise<boolean> {
  enabled ??= fetch('/api/course/tts', { credentials: 'same-origin' })
    .then((r) => (r.ok ? (r.json() as Promise<{ enabled: boolean }>) : { enabled: false }))
    .then((j) => j.enabled)
    .catch(() => false)
  return enabled
}

/** Files that turned out not to exist yet — not asked for again this visit. */
const missing = new Set<string>()

/*
 * One audio element for every clip. iPhones only let a page play sound
 * from an element a tap has already started, so reusing it is what lets a
 * dialogue play line after line.
 */
let player: HTMLAudioElement | null = null
let finish: (() => void) | null = null

function playFile(key: string, rate: number): Promise<boolean> {
  if (typeof Audio === 'undefined') return Promise.resolve(false)
  player ??= new Audio()
  const a = player
  return new Promise((resolve) => {
    let started = false
    const done = (ok: boolean) => {
      a.onended = null
      a.onerror = null
      finish = null
      resolve(ok)
    }
    finish = () => done(true)
    a.onended = () => done(true)
    a.onerror = () => {
      if (!started) missing.add(key)
      done(started)
    }
    a.src = `/api/course/tts/${key}`
    a.playbackRate = rate
    a.play().then(
      () => {
        started = true
      },
      () => done(false),
    )
  })
}

/* ── public ──────────────────────────────────────────────────────────── */

/** Whether anything can be read aloud here: a recording, or a Polish device voice. */
export function usePolishVoice(): boolean {
  const [ok, setOk] = useState(() => polishVoice() !== null)
  useEffect(() => {
    let live = true
    recordings().then((r) => live && r && setOk(true))
    const s = synth()
    if (!s) return
    const update = () => setOk((was) => was || polishVoice() !== null)
    update()
    s.addEventListener('voiceschanged', update)
    return () => {
      live = false
      s.removeEventListener('voiceschanged', update)
    }
  }, [])
  return ok
}

/**
 * Says one piece of Polish; resolves when it ends (or is stopped).
 * `slow` is the listening step's slower pace. `voice` is the speaker's, from
 * dialogueVoices(); words and narration use the default.
 */
export async function speak(text: string, { slow = false, voice = DEFAULT_ROLE }: { slow?: boolean; voice?: TtsRole } = {}): Promise<void> {
  const clean = speakable(text)
  if (!clean) return
  const key = ttsKey(voice, clean)
  if (!missing.has(key) && (await recordings())) {
    if (await playFile(key, slow ? 0.8 : 1)) return
  }
  const s = synth()
  const device = polishVoice()
  if (!s || !device) return
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(clean)
    u.voice = device
    u.lang = device.lang
    u.rate = slow ? 0.72 : 0.92
    u.onend = () => resolve()
    u.onerror = () => resolve()
    s.speak(u)
  })
}

export function stopSpeaking(): void {
  synth()?.cancel()
  if (player && !player.paused) player.pause()
  finish?.()
}
